/**
 * SEO loop client approval page: /a/:token on the customer host. Public (no login): the token is the only credential and
 * the server decides what is shown (client-safe view only). Calls go through the plain BFF passthrough
 * (/bff/v1/seo-loop/public/approvals/...); the worker sends no Referer for this page (security.ts isNoReferrerPath).
 *
 * Phone first: a sticky progress header, the sections in order (clinic, site fixes, treatments, pages, access), one
 * control per item kind, autosave per item, then Send answers and a confirmation screen. English and Arabic (RTL).
 * Brand tokens only. Never says "Medically reviewed by" (owner decision, Option A). Owner: seo-loop.
 */
import './seo-approval.css';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { BrandRoot, Badge, Button, Heading, Loading, SkipLink, Text } from '@qm/ui';
import { assets } from '@qm/brand';
import {
  APPROVAL_SECTION_ORDER, ApprovalAnswerInputSchema, type ApprovalAnswerInput, type PublicApprovalAnswer,
  type PublicApprovalItem, type PublicApprovalRequest,
} from '@qm/contracts/seo-loop';
import { useApp } from '../../../lib/app-context';
import { bff, BffError } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { LanguageToggle } from '../../../shell/Shell';

const base = (token: string) => `/v1/seo-loop/public/approvals/${encodeURIComponent(token)}` as const;

/** API calls of this page (replaced in tests). */
export interface SeoApprovalApi {
  load(): Promise<PublicApprovalRequest>;
  save(itemId: string, input: ApprovalAnswerInput): Promise<PublicApprovalAnswer>;
  submit(): Promise<PublicApprovalRequest>;
}
export function seoApprovalApi(token: string): SeoApprovalApi {
  return {
    load: () => bff<PublicApprovalRequest>(base(token)),
    save: (itemId, input) => bff<PublicApprovalAnswer>(`${base(token)}/items/${encodeURIComponent(itemId)}`, { method: 'PATCH', body: JSON.stringify(input) }),
    submit: () => bff<PublicApprovalRequest>(`${base(token)}/submit`, { method: 'POST', body: '{}' }),
  };
}

/** What the client can act on. A 404 never says whether a link existed; 410 says why it stopped working. */
export function failureKey(err: unknown): string {
  const status = err instanceof BffError ? err.status : 0;
  const reason = err instanceof BffError ? (err.envelope?.error.details as { reason?: string } | undefined)?.reason : undefined;
  if (status === 410) return reason === 'link_revoked' ? 'seo-loop.approve.revoked' : 'seo-loop.approve.expired';
  if (status === 404) return 'seo-loop.approve.invalid';
  if (status === 429) return 'seo-loop.approve.rateLimited';
  if (status === 409) return 'seo-loop.approve.alreadySent';
  return 'seo-loop.approve.failed';
}

export function SeoApprovalPage() {
  const { token = '' } = useParams();
  const api = useMemo(() => seoApprovalApi(token), [token]);
  return <SeoApprovalFrame><SeoApprovalLoader api={api} /></SeoApprovalFrame>;
}

/** Minimal public frame: lockup, language switch, main. No shell, menus or session calls. */
export function SeoApprovalFrame({ children }: { children: ReactNode }) {
  const { t, locale, dir } = useApp();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);
  return (
    <BrandRoot dir={dir} lang={locale}>
      <div className="qsa" data-layout="seo-approval">
        <SkipLink>{t('shell.skipToContent')}</SkipLink>
        <header className="qsa__bar">
          <img className="qsa__lockup" src={assets.lockup} alt={t('common.brand')} width={168} height={28} />
          <LanguageToggle />
        </header>
        <main id="main" className="qsa__main" tabIndex={-1}>{children}</main>
      </div>
    </BrandRoot>
  );
}

function SeoApprovalLoader({ api }: { api: SeoApprovalApi }) {
  const { t } = useApp();
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'error'; key: string } | { kind: 'ready'; data: PublicApprovalRequest }>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    api.load().then((data) => { if (live) setState({ kind: 'ready', data }); }, (e: unknown) => { if (live) setState({ kind: 'error', key: failureKey(e) }); });
    return () => { live = false; };
  }, [api]);
  if (state.kind === 'loading') return <><Heading level={1}>{t('seo-loop.approve.pageTitle')}</Heading><Loading label={t('seo-loop.approve.loading')} /></>;
  if (state.kind === 'error') {
    return (
      <>
        <Heading level={1}>{t('seo-loop.approve.pageTitle')}</Heading>
        <p className="qm-error" role="alert" data-testid="seo-approval-unavailable">{t(state.key)}</p>
      </>
    );
  }
  return <SeoApprovalForm initial={state.data} api={api} />;
}

type SaveState = 'saving' | 'saved' | 'failed';

/** The questions, progress and submit. Exported for tests (rendered with fixed data). */
export function SeoApprovalForm({ initial, api }: { initial: PublicApprovalRequest; api: SeoApprovalApi }) {
  const { t } = useApp();
  const fmt = useFormat();
  const [data, setData] = useState(initial);
  const [answers, setAnswers] = useState<Record<string, PublicApprovalAnswer | null>>(() => Object.fromEntries(initial.items.map((i) => [i.id, i.answer])));
  const [saving, setSaving] = useState<Record<string, SaveState>>({});
  const [showMissing, setShowMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const seq = useRef<Record<string, number>>({});

  const answered = data.items.filter((i) => answers[i.id]).length;
  const missing = data.items.filter((i) => i.required && !answers[i.id]);

  const save = useCallback(async (itemId: string, input: ApprovalAnswerInput) => {
    const n = (seq.current[itemId] ?? 0) + 1;
    seq.current[itemId] = n;
    setSaving((s) => ({ ...s, [itemId]: 'saving' }));
    try {
      const saved = await api.save(itemId, input);
      if (seq.current[itemId] !== n) return; // a newer edit of this item is in flight
      setAnswers((a) => ({ ...a, [itemId]: saved }));
      setSaving((s) => ({ ...s, [itemId]: 'saved' }));
    } catch (e) {
      if (seq.current[itemId] !== n) return;
      setSaving((s) => ({ ...s, [itemId]: 'failed' }));
      const key = failureKey(e);
      if (key !== 'seo-loop.approve.failed') setSubmitError(key);
    }
  }, [api]);

  const submit = async () => {
    if (missing.length) { setShowMissing(true); return; }
    setSubmitting(true);
    setSubmitError(null);
    try { setData(await api.submit()); } catch (e) { setSubmitError(failureKey(e)); } finally { setSubmitting(false); }
  };

  if (data.status === 'completed') {
    return (
      <section aria-labelledby="seo-approval-done" data-testid="seo-approval-done">
        <Heading level={1} id="seo-approval-done">{t('seo-loop.approve.doneTitle')}</Heading>
        <Text>{t('seo-loop.approve.doneBody')}</Text>
        {data.completedAt ? <Text tone="muted">{t('seo-loop.approve.doneAt', { date: fmt.date(data.completedAt) })}</Text> : null}
      </section>
    );
  }

  const sections = APPROVAL_SECTION_ORDER.map((s) => ({ section: s, items: data.items.filter((i) => i.section === s) })).filter((g) => g.items.length);
  return (
    <>
      <header className="qsa__head" data-testid="seo-approval-progress">
        <Heading level={1}>{data.title}</Heading>
        {data.businessName ? <Text tone="muted">{t('seo-loop.approve.for', { business: data.businessName })}</Text> : null}
        <progress className="qsa__progress" max={data.items.length} value={answered} aria-label={t('seo-loop.approve.progressLabel')} />
        <Text aria-live="polite">
          {t('seo-loop.approve.progress', { answered, total: data.items.length })}
          {missing.length ? ` · ${t('seo-loop.approve.requiredLeft', { count: missing.length })}` : ''}
        </Text>
      </header>
      <Text>{t('seo-loop.approve.intro')}</Text>
      <Text tone="muted">{t('seo-loop.approve.expires', { date: fmt.date(data.expiresAt) })} {t('seo-loop.approve.privacy')}</Text>
      {sections.map((g) => (
        <section key={g.section} className="qsa__section" aria-labelledby={`seo-section-${g.section}`}>
          <Heading level={2} id={`seo-section-${g.section}`}>{t(`seo-loop.approve.section.${g.section}`)}</Heading>
          {g.section === 'treatment' ? <TreatmentGroups items={g.items} answers={answers} saving={saving} showMissing={showMissing} onSave={save} />
            : g.items.map((i) => <ItemCard key={i.id} item={i} answer={answers[i.id] ?? null} state={saving[i.id]} missing={showMissing && i.required && !answers[i.id]} onSave={save} />)}
        </section>
      ))}
      <div className="qsa__submit">
        {showMissing && missing.length ? <p className="qm-error" role="alert">{t('seo-loop.approve.submitBlocked', { count: missing.length })}</p> : null}
        {submitError ? <p className="qm-error" role="alert" data-testid="seo-approval-error">{t(submitError)}</p> : null}
        <Button variant="primary" onClick={() => void submit()} disabled={submitting} data-testid="seo-approval-submit">
          {submitting ? t('seo-loop.approve.submitting') : t('seo-loop.approve.submit')}
        </Button>
      </div>
    </>
  );
}

function TreatmentGroups({ items, answers, saving, showMissing, onSave }: {
  items: PublicApprovalItem[]; answers: Record<string, PublicApprovalAnswer | null>; saving: Record<string, SaveState>; showMissing: boolean;
  onSave: (id: string, a: ApprovalAnswerInput) => Promise<void>;
}) {
  const names = [...new Set(items.map((i) => i.treatmentName ?? ''))];
  return (
    <>
      {names.map((name, n) => (
        <div key={name || n} className="qsa__group" role="group" aria-labelledby={`seo-treatment-${n}`}>
          <Heading level={3} id={`seo-treatment-${n}`}>{name}</Heading>
          {items.filter((i) => (i.treatmentName ?? '') === name).map((i) => (
            <ItemCard key={i.id} item={i} answer={answers[i.id] ?? null} state={saving[i.id]} missing={showMissing && i.required && !answers[i.id]} onSave={onSave} />
          ))}
        </div>
      ))}
    </>
  );
}

/** One question: prompt in the reader's language, the control for its kind, an optional note and the save status. */
export function ItemCard({ item, answer, state, missing, onSave }: {
  item: PublicApprovalItem; answer: PublicApprovalAnswer | null; state: SaveState | undefined; missing: boolean;
  onSave: (id: string, a: ApprovalAnswerInput) => Promise<void>;
}) {
  const { t, locale } = useApp();
  const prompt = locale === 'ar' ? item.promptAr : item.promptEn;
  const promptId = `seo-q-${item.id}`;
  const [note, setNote] = useState(answer?.note ?? '');
  const withNote = (a: ApprovalAnswerInput): ApprovalAnswerInput => (note.trim() ? { ...a, note: note.trim() } : a);
  const send = (a: ApprovalAnswerInput) => {
    const parsed = ApprovalAnswerInputSchema.safeParse(withNote(a));
    if (parsed.success) void onSave(item.id, parsed.data);
  };
  // re-send the current answer with the new note
  const saveNote = () => { if (answer && (answer.note ?? '') !== note.trim()) send(fromAnswer(answer)); };

  return (
    <div className="qsa-item" data-testid="seo-approval-item" data-kind={item.kind} data-missing={missing ? 'true' : undefined}>
      <p className="qsa-item__prompt" id={promptId}>{prompt}</p>
      <div className="qsa-item__meta">
        <Badge tone={item.required ? 'accent' : 'neutral'}>{item.required ? t('seo-loop.approve.required') : t('seo-loop.approve.optional')}</Badge>
      </div>
      <KindControl item={item} answer={answer} labelledBy={promptId} onAnswer={send} />
      {answer ? (
        <label className="qm-field">
          <span className="qm-field__label">{t('seo-loop.approve.note')}</span>
          <textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} onBlur={saveNote} />
        </label>
      ) : null}
      {missing ? <p className="qm-field__error" role="alert">{t('seo-loop.approve.missing')}</p> : null}
      <p className="qsa-item__status" aria-live="polite">
        {state === 'saving' ? t('seo-loop.approve.saving') : state === 'saved' ? t('seo-loop.approve.saved') : state === 'failed' ? t('seo-loop.approve.saveFailed') : ''}
      </p>
    </div>
  );
}

function fromAnswer(a: PublicApprovalAnswer): ApprovalAnswerInput {
  switch (a.answer) {
    case 'amount': return { answer: 'amount', value: Number(a.value) };
    case 'corrected': case 'text': case 'choice': return { answer: a.answer, value: a.value ?? '' };
    default: return { answer: a.answer };
  }
}

/** True when the text is a non-negative AED amount with at most 2 decimals. */
export function validAed(s: string): boolean {
  return /^\d{1,8}(\.\d{1,2})?$/.test(s.trim());
}

function KindControl({ item, answer, labelledBy, onAnswer }: { item: PublicApprovalItem; answer: PublicApprovalAnswer | null; labelledBy: string; onAnswer: (a: ApprovalAnswerInput) => void }) {
  const { t, locale } = useApp();
  const name = `seo-${item.id}`;
  const [text, setText] = useState(answer && answer.answer !== 'confirmed' && answer.answer !== 'hide_price' ? answer.value ?? '' : '');
  const [mode, setMode] = useState<string | null>(answer?.answer ?? null);
  const [aedError, setAedError] = useState(false);
  const radio = (value: string, label: string, onPick: () => void) => (
    <label className="qsa-choice" key={value}>
      <input type="radio" name={name} value={value} checked={mode === value} onChange={() => { setMode(value); onPick(); }} />
      {label}
    </label>
  );

  switch (item.kind) {
    case 'yes_no':
      return (
        <fieldset aria-labelledby={labelledBy}>
          {radio('yes', t('seo-loop.approve.yes'), () => onAnswer({ answer: 'yes' }))}
          {radio('no', t('seo-loop.approve.no'), () => onAnswer({ answer: 'no' }))}
        </fieldset>
      );
    case 'choice':
      return (
        <fieldset aria-labelledby={labelledBy}>
          {(item.options ?? []).map((o) => (
            <label className="qsa-choice" key={o.value}>
              <input type="radio" name={name} value={o.value} checked={mode === 'choice' && text === o.value}
                onChange={() => { setMode('choice'); setText(o.value); onAnswer({ answer: 'choice', value: o.value }); }} />
              {locale === 'ar' ? o.label_ar : o.label_en}
            </label>
          ))}
        </fieldset>
      );
    case 'text':
      return (
        <textarea aria-labelledby={labelledBy} rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
          onBlur={() => { if (text.trim() && text.trim() !== (answer?.value ?? '')) onAnswer({ answer: 'text', value: text.trim() }); }} />
      );
    case 'confirm':
      return (
        <fieldset aria-labelledby={labelledBy}>
          <Text tone="muted">{item.currentValue ? t('seo-loop.approve.onFile', { value: item.currentValue }) : t('seo-loop.approve.notOnFile')}</Text>
          {item.currentValue ? radio('confirmed', t('seo-loop.approve.confirmYes'), () => onAnswer({ answer: 'confirmed' })) : null}
          {item.currentValue ? radio('corrected', t('seo-loop.approve.confirmNo'), () => undefined) : null}
          {!item.currentValue || mode === 'corrected' ? (
            <label className="qm-field">
              <span className="qm-field__label">{t('seo-loop.approve.enterValue')}</span>
              <input type="text" maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
                onBlur={() => { if (text.trim() && text.trim() !== (answer?.value ?? '')) { setMode('corrected'); onAnswer({ answer: 'corrected', value: text.trim() }); } }} />
            </label>
          ) : null}
        </fieldset>
      );
    case 'number_aed':
      return (
        <fieldset aria-labelledby={labelledBy}>
          {radio('amount', t('seo-loop.approve.showPrice'), () => { if (validAed(text)) onAnswer({ answer: 'amount', value: Number(text) }); })}
          {mode === 'amount' ? (
            <label className="qm-field">
              <span className="qm-field__label">{t('seo-loop.approve.amount')}</span>
              <input type="number" inputMode="decimal" min={0} step="0.01" value={text} aria-invalid={aedError || undefined}
                onChange={(e) => setText(e.target.value)}
                onBlur={() => {
                  if (!text.trim()) return;
                  const ok = validAed(text);
                  setAedError(!ok);
                  if (ok && text.trim() !== (answer?.value ?? '')) onAnswer({ answer: 'amount', value: Number(text) });
                }} />
              <span className="qm-field__hint">{t('seo-loop.approve.amountHint')}</span>
              {aedError ? <span className="qm-field__error" role="alert">{t('seo-loop.approve.amountInvalid')}</span> : null}
            </label>
          ) : null}
          {radio('hide_price', t('seo-loop.approve.hidePrice'), () => onAnswer({ answer: 'hide_price' }))}
        </fieldset>
      );
  }
}
